import React, { createContext, useContext, ReactNode } from "react";
import classNames from "classnames";
import Typography from "@/components/kit/Typography";
import SvgIcon from "@/components/common/SvgIcon";

// Types
interface StepperContextType {
  currentStep: number;
  totalSteps: number;
}

interface StepperProps {
  currentStep: number;
  children: ReactNode;
  className?: string;
}

interface StepProps {
  stepNumber: number;
  title?: string;
  icon?: ReactNode;
  isClickable?: boolean;
  onClick?: () => void;
  className?: string;
  children?: ReactNode;
}

interface StepConnectorProps {
  className?: string;
}

// Context
const StepperContext = createContext<StepperContextType | null>(null);

const useStepperContext = () => {
  const context = useContext(StepperContext);
  if (!context) {
    throw new Error("Stepper components must be used within a Stepper");
  }
  return context;
};

// Main Stepper Component
const Stepper = ({ currentStep, children, className }: StepperProps) => {
  const childrenArray = React.Children.toArray(children);
  const totalSteps = childrenArray.filter(
    (child) => React.isValidElement(child) && child.type === Step,
  ).length;

  const contextValue: StepperContextType = {
    currentStep,
    totalSteps,
  };

  return (
    <StepperContext.Provider value={contextValue}>
      <div className={classNames("flex flex-col gap-10 w-full", className)}>
        {children}
      </div>
    </StepperContext.Provider>
  );
};

// Step Component
const Step = ({
  stepNumber,
  title,
  icon,
  isClickable = false,
  onClick,
  className,
  children,
}: StepProps) => {
  const { currentStep, totalSteps } = useStepperContext();

  const isCompleted = currentStep > stepNumber;
  const isActive = currentStep === stepNumber;

  const handleClick = () => {
    if (isClickable && onClick) {
      onClick();
    }
  };

  const stepContent = children || (
    <Typography
      color={currentStep >= stepNumber ? "text-gray-600" : "text-gray-300"}
      variant="Body1-Regular"
    >
      {title}
    </Typography>
  );

  return (
    <div className={classNames("relative", className)}>
      <div
        className={classNames(
          "rounded-lg rounded-r-lg overflow-hidden transition-all border-l-[8px] duration-300",
          {
            "cursor-pointer": isClickable,
            "border-blue-500 border-l-[8px]": isActive,
            "border-transparent": !isActive,
          },
        )}
        onClick={handleClick}
      >
        <div
          className={classNames(
            "flex rounded-r-lg p-4 py-3 items-center gap-4 bg-gray-50",
            {
              "bg-blue-50 border-blue-500": isActive,
              "bg-white": !isActive,
            },
          )}
        >
          {/* Step Icon/Number */}
          {isCompleted ? (
            icon || (
              <SvgIcon
                iconName="checkCircleFilled"
                fill="fill-green-600"
                className="w-6 h-6"
              />
            )
          ) : (
            <div
              className={classNames(
                "w-6 h-6 rounded-full flex items-center justify-center shrink-0",
                {
                  "bg-white border-2 border-blue-600 text-blue-600": isActive,
                  "bg-white border-2 border-gray-200": !isActive,
                },
              )}
            >
              <Typography
                color={
                  currentStep >= stepNumber ? "text-blue-500" : "text-gray-300"
                }
                variant="Body2-Bold"
              >
                {stepNumber}
              </Typography>
            </div>
          )}

          {/* Step Content */}
          <div className="flex-1">{stepContent}</div>
        </div>
      </div>

      {/* Connector */}
      {stepNumber < totalSteps && <StepConnector />}
    </div>
  );
};

// Step Connector Component
const StepConnector = ({ className }: StepConnectorProps) => {
  return (
    <div
      className={classNames(
        "absolute left-9 top-full h-8 border-l-2 border-dashed border-gray-500",
        className,
      )}
    />
  );
};

// Custom Step Content Component (for advanced customization)
const StepContent = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div className={classNames("step-custom-content", className)}>
      {children}
    </div>
  );
};

// Compound component exports
Stepper.Step = Step;
Stepper.Connector = StepConnector;
Stepper.Content = StepContent;

export default Stepper;
